import React from "react";
import { useTranslation } from "react-i18next";
import "./styles/index.css";
import "./styles/pages/AdminPage.css";
import { HostTeamInfo, AppSettings, GraphUser, Administrator, TeamPrivacyType, TeamPrivacy } from "./types";
import { apiService } from "./services/api.service";
import PeoplePicker from "./PeoplePicker";
import ApprovedRequestsManager from "./ApprovedRequestsManager";
import { SubscriptionCard } from "./components/SubscriptionCard";
import { buildApiUrlWithParams } from "./utils/api-url";

interface AdminPageProps {
  theme?: string;
  teamId: string | null;
  currentUserId?: string;
}

export default function AdminPage({ theme = "default", teamId, currentUserId }: AdminPageProps) {
  const { i18n, t } = useTranslation('adminPage');
  
  // 🔍 DEBUG: Log component mount and language changes
  React.useEffect(() => {
    console.log('[AdminPage] Component mounted with language:', i18n.language);
    
    const handleLanguageChange = (lng: string) => {
      console.log('[AdminPage] Language changed to:', lng);
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const [teamInfo, setTeamInfo] = React.useState<HostTeamInfo | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [settings, setSettings] = React.useState<AppSettings | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const [selectedApprovers, setSelectedApprovers] = React.useState<GraphUser[]>([]);
  const [showPeoplePicker, setShowPeoplePicker] = React.useState(false);

  // États pour les administrateurs
  const [administrators, setAdministrators] = React.useState<Administrator[]>([]);
  const [showAdminPeoplePicker, setShowAdminPeoplePicker] = React.useState(false);

  // États pour les types d'équipes
  const [allPrivacyTypes, setAllPrivacyTypes] = React.useState<TeamPrivacyType[]>([]);
  const [enabledPrivacyTypes, setEnabledPrivacyTypes] = React.useState<TeamPrivacy[]>([]);

  // État pour la modal d'avertissement
  const [warningModal, setWarningModal] = React.useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "warning" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "warning"
  });

  // État pour la modal de confirmation de changement de mode d'approbation
  const [approvalModeConfirmModal, setApprovalModeConfirmModal] = React.useState<{
    isOpen: boolean;
    newMode: "basic" | "basicAwaitAll" | null;
    impactedRequests: Array<{
      id: string;
      teamName: string;
      approvedCount: number;
      totalApprovers: number;
      requesterEmail: string;
      privacy: TeamPrivacy;
    }>;
  }>({
    isOpen: false,
    newMode: null,
    impactedRequests: []
  });

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

  // Fonction helper pour afficher une modal d'avertissement
  const showWarning = (title: string, message: string, type: "warning" | "error" = "warning") => {
    setWarningModal({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeWarningModal = () => {
    setWarningModal({
      ...warningModal,
      isOpen: false
    });
  };

  React.useEffect(() => {
    // Charger les paramètres même sans équipe détectée
    loadSettings();
    
    // Si une équipe est détectée, charger ses infos aussi
    if (teamId) {
      loadTeamInfo();
    } else {
      // Mode autonome - pas besoin d'équipe
      setLoading(false);
    }
  }, [teamId]);


  const loadTeamInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildApiUrlWithParams('/team/info', { teamId: teamId || '' }));
      const data = await response.json();

      if (data.success && data.data) {
        setTeamInfo(data.data);
      } else {
        setError(data.error || t('messages.error'));
      }
    } catch (err: any) {
      console.error("Error loading team info:", err);
      setError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const resp = await apiService.getSettings();
      if (resp.success) {
        const loadedSettings = resp.data || { enabledTemplates: [], notificationSettings: {}, azureTenantId: '' };
        setSettings(loadedSettings);

        // Charger les approbateurs définis dans les paramètres
        if (loadedSettings.approvers && loadedSettings.approvers.length > 0) {
          setSelectedApprovers(loadedSettings.approvers.map(approver => ({
            id: approver.id,
            displayName: approver.displayName,
            mail: approver.email,
            userPrincipalName: approver.email
          })));
        }

        // Charger les types de confidentialité activés
        setEnabledPrivacyTypes(loadedSettings.enabledPrivacyTypes || ['private', 'public']);
      }

      // Charger les administrateurs
      const adminsResp = await apiService.getAllAdministrators();
      if (adminsResp.success && adminsResp.data) {
        setAdministrators(adminsResp.data);
      }

      // Charger tous les types de confidentialité disponibles
      const typesResp = await apiService.getAllTeamPrivacyTypes();
      if (typesResp.success && typesResp.data) {
        setAllPrivacyTypes(typesResp.data);
      }

    } catch (e) {
      console.error("Error loading settings", e);
    }
  };

  // Helper function to get privacy type label with icon
  const getPrivacyLabel = (privacy: TeamPrivacy): string => {
    switch (privacy) {
      case 'private':
        return t('messages.privacyLabels.private');
      case 'public':
        return t('messages.privacyLabels.public');
      default:
        return privacy;
    }
  };

  const onChangeApprovalMode = async (mode: "basic" | "basicAwaitAll") => {
    if (!settings) return;

    const currentMode = settings.notificationSettings?.approvalMode || 'basic';

    // Si on passe de basicAwaitAll à basic, afficher la modal de confirmation
    if (currentMode === 'basicAwaitAll' && mode === 'basic') {
      // Récupérer les demandes en attente qui seront impactées
      try {
        const requestsResp = await apiService.getAllRequests();
        if (requestsResp.success && requestsResp.data) {
          const pendingRequests = requestsResp.data.filter((r: any) => r.status === 'Pending');

          // Trouver les demandes qui ont au moins une approbation
          const impactedRequests = pendingRequests
            .filter((r: any) => {
              const responses = r.approvalResponses || [];
              if (responses.length === 0) return false;

              const approvedCount = responses.filter((resp: any) => resp.response === 'approved').length;
              return approvedCount > 0 && approvedCount < responses.length;
            })
            .map((r: any) => {
              const responses = r.approvalResponses || [];
              const approvedCount = responses.filter((resp: any) => resp.response === 'approved').length;

              return {
                id: r.id,
                teamName: r.teamName,
                approvedCount,
                totalApprovers: responses.length,
                requesterEmail: r.requesterEmail,
                privacy: r.privacy
              };
            });

          // Afficher la modal de confirmation avec la liste des demandes impactées
          setApprovalModeConfirmModal({
            isOpen: true,
            newMode: mode,
            impactedRequests
          });
          return; // Attendre la confirmation de l'utilisateur
        }
      } catch (err: any) {
        console.error("Error fetching requests:", err);
      }
    }

    // Sinon, procéder directement au changement
    await confirmApprovalModeChange(mode);
  };

  const confirmApprovalModeChange = async (mode: "basic" | "basicAwaitAll") => {
    if (!settings) return;
    setSaving(true);
    setSaveSuccess(false);

    // Fermer la modal de confirmation
    setApprovalModeConfirmModal({
      isOpen: false,
      newMode: null,
      impactedRequests: []
    });

    try {
      const newSettings: AppSettings = {
        ...settings,
        notificationSettings: { ...(settings.notificationSettings || {}), approvalMode: mode },
      };
      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);

        // Déclencher automatiquement la réévaluation des demandes en cours
        try {
          console.log("Triggering reevaluation of pending requests...");
          const reevalResp = await fetch(buildApiUrlWithParams('/admin/reevaluate-requests'), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (reevalResp.ok) {
            const reevalData = await reevalResp.json();
            console.log("Reevaluation completed:", reevalData.data);

            if (reevalData.data && (reevalData.data.approved > 0 || reevalData.data.rejected > 0)) {
              showWarning(
                t('messages.reevaluationCompleted'),
                t('messages.reevaluationMessage', {
                  processed: reevalData.data.processed,
                  approved: reevalData.data.approved,
                  rejected: reevalData.data.rejected
                }),
                "warning"
              );
            }
          }
        } catch (reevalErr: any) {
          console.warn("Error during reevaluation:", reevalErr);
          // Ne pas bloquer l'UI si la réévaluation échoue
        }

        setSaveSuccess(true);
        // Masquer la notification après 3 secondes
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err: any) {
      console.error("Error saving approval mode:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const cancelApprovalModeChange = () => {
    setApprovalModeConfirmModal({
      isOpen: false,
      newMode: null,
      impactedRequests: []
    });
  };

  const handleAddApprover = async (user: GraphUser) => {
    if (!settings || selectedApprovers.find(u => u.id === user.id)) return;
    
    const newApprovers = [...selectedApprovers, user];
    setSelectedApprovers(newApprovers);
    setShowPeoplePicker(false);
    
    // Sauvegarder immédiatement
    setSaving(true);
    try {
      const newSettings: AppSettings = {
        ...settings,
        approvers: newApprovers.map(u => ({ id: u.id, email: u.mail || u.userPrincipalName, displayName: u.displayName, addedDate: new Date().toISOString() }))
      };
      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err) {
      console.error("Error adding approver:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveApprover = async (userId: string) => {
    if (!settings) return;

    // Empêcher la suppression du dernier approbateur
    if (selectedApprovers.length <= 1) {
      showWarning(
        t('messages.cannotRemoveLastApprover'),
        t('messages.cannotRemoveLastApproverMessage'),
        "error"
      );
      return;
    }

    const newApprovers = selectedApprovers.filter(u => u.id !== userId);
    setSelectedApprovers(newApprovers);

    // Sauvegarder immédiatement
    setSaving(true);
    try {
      const newSettings: AppSettings = {
        ...settings,
        approvers: newApprovers.map(u => ({ id: u.id, email: u.mail || u.userPrincipalName, displayName: u.displayName, addedDate: new Date().toISOString() }))
      };
      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err) {
      console.error("Error removing approver:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  // ==================== GESTION DES ADMINISTRATEURS ====================

  const handleAddAdministrator = async (user: GraphUser) => {
    if (administrators.find(a => a.id === user.id)) return;

    setSaving(true);
    try {
      const newAdmin: Administrator = {
        id: user.id,
        email: user.mail || user.userPrincipalName,
        displayName: user.displayName,
        addedDate: new Date().toISOString()
      };

      const resp = await apiService.addAdministrator(newAdmin);
      if (resp.success && resp.data) {
        setAdministrators([...administrators, resp.data]);
        setShowAdminPeoplePicker(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err) {
      console.error("Error adding administrator:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAdministrator = async (adminId: string) => {
    // Ne pas permettre de supprimer le dernier administrateur
    if (administrators.length === 1) {
      showWarning(
        t('messages.cannotRemoveLastAdmin'),
        t('messages.cannotRemoveLastAdminMessage'),
        "warning"
      );
      return;
    }

    // Ne pas permettre de se retirer soi-même
    if (currentUserId && adminId === currentUserId) {
      showWarning(
        t('messages.cannotRemoveSelf'),
        t('messages.cannotRemoveSelfMessage'),
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      const response = await apiService.removeAdministrator(adminId);

      if (!response.success) {
        // Si l'API retourne une erreur (par exemple auto-suppression détectée côté serveur)
        showWarning(
          t('messages.recordSaveError'),
          response.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
        return;
      }

      setAdministrators(administrators.filter(a => a.id !== adminId));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error("Error removing administrator:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePrivacyType = async (typeId: TeamPrivacy) => {
    if (!settings) return;

    // Ne pas permettre de désactiver le dernier type
    if (enabledPrivacyTypes.length === 1 && enabledPrivacyTypes.includes(typeId)) {
      showWarning(
        t('messages.cannotDisableLastPrivacyType'),
        t('messages.cannotDisableLastPrivacyTypeMessage'),
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      const newEnabledTypes = enabledPrivacyTypes.includes(typeId)
        ? enabledPrivacyTypes.filter(t => t !== typeId)
        : [...enabledPrivacyTypes, typeId];

      const newSettings: AppSettings = {
        ...settings,
        enabledPrivacyTypes: newEnabledTypes,
      };

      const resp = await apiService.updateSettings(newSettings);
      if (resp.success && resp.data) {
        setSettings(resp.data);
        setEnabledPrivacyTypes(newEnabledTypes);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        // L'API a retourné une erreur
        showWarning(
          t('messages.recordSaveError'),
          resp.error || t('messages.recordSaveErrorMessage'),
          "error"
        );
      }
    } catch (err: any) {
      console.error("Error toggling privacy type:", err);
      showWarning(
        t('messages.recordSaveError'),
        t('messages.recordSaveUnexpectedError'),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className={`setup-page theme-${theme}`}>
        <div className="setup-loading">
          <div className="spinner"></div>
          <p>{t('messages.loading', 'Chargement des informations...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`setup-page theme-${theme}`}>
        <div className="setup-header">
          <h1>{t('title')}</h1>
        </div>
        <div className="setup-section">
          <div className="errors-section">
            <p>⚠️ {error}</p>
            <p className="admin-section-margin">
              {t('messages.error', 'Assurez-vous que l\'application est correctement installée dans une équipe Teams.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`setup-page theme-${theme}`}>
      {/* Notification de succès - Position sticky sous le menu de navigation */}
      {saveSuccess && (
        <div className="success-card admin-success-sticky">
          <span className="icon-text">✓</span>
          <span>{t('messages.saved', 'Paramètres enregistrés avec succès')}</span>
        </div>
      )}

      <div className="setup-header">
        <h1>{t('title')}</h1>
        <p>{t('header.description')}</p>
      </div>

      {/* Subscription Management Card */}
      <SubscriptionCard onLicenseActivated={loadSettings} />

      {/* Nom de l'application */}
      <div className="setup-section">
        <h2>{t('sections.applicationName.title')}</h2>
        <p className="help-text">
          {t('sections.applicationName.description')}
        </p>

        <div className="status-card">
          <div className="status-content">
            <label className="form-label">
              {t('sections.applicationName.label')}
            </label>
            <input
              type="text"
              placeholder={t('sections.applicationName.placeholder')}
              value={settings?.applicationName || ""}
              onChange={(e) => {
                if (!settings) return;
                const newSettings = { ...settings, applicationName: e.target.value };
                setSettings(newSettings);
              }}
              onBlur={async (e) => {
                if (!settings) return;
                setSaving(true);
                setSaveSuccess(false);
                try {
                  const resp = await apiService.updateSettings({ ...settings, applicationName: e.target.value });
                  if (resp.success && resp.data) {
                    setSettings(resp.data);
                    setSaveSuccess(true);
                    setTimeout(() => setSaveSuccess(false), 3000);
                  } else {
                    showWarning(
                      t('messages.recordSaveError'),
                      resp.error || t('messages.recordSaveErrorMessage'),
                      "error"
                    );
                  }
                } catch (err) {
                  console.error("Error saving application name:", err);
                  showWarning(
                    t('messages.recordSaveError'),
                    t('messages.recordSaveUnexpectedError'),
                    "error"
                  );
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              className="fluent-input form-input"
            />
            <div className="form-hint">
              {t('sections.applicationName.hint')}
            </div>

            {saving && (
              <div className="loading-message">
                <div className="spinner-small"></div>
                <span>{t('messages.saving')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Création automatique des équipes */}
      <div className="setup-section">
        <h2>{t('sections.autoCreate.title')}</h2>
        <p className="help-text">
          {t('sections.autoCreate.description')}
        </p>

        <div className="status-card">
          <div className="status-content">
            <div className="checkbox-container">
              <input
                type="checkbox"
                id="autoCreateOnApproval"
                checked={settings?.autoCreateOnApproval ?? true}
                onChange={async (e) => {
                  if (!settings) return;
                  setSaving(true);
                  setSaveSuccess(false);
                  try {
                    const newSettings = { ...settings, autoCreateOnApproval: e.target.checked };
                    const resp = await apiService.updateSettings(newSettings);
                    if (resp.success && resp.data) {
                      setSettings(resp.data);
                      setSaveSuccess(true);
                      setTimeout(() => setSaveSuccess(false), 3000);
                    } else {
                      showWarning(
                        t('messages.recordSaveError'),
                        resp.error || t('messages.recordSaveErrorMessage'),
                        "error"
                      );
                    }
                  } catch (err) {
                    console.error("Error saving auto-create setting:", err);
                    showWarning(
                      t('messages.recordSaveError'),
                      t('messages.recordSaveUnexpectedError'),
                      "error"
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                className="admin-checkbox-width"
              />
              <label htmlFor="autoCreateOnApproval" className="checkbox-label">
                {t('sections.autoCreate.label')}
              </label>
            </div>

            <div className="section-description">
              <strong>{t('sections.autoCreate.enabledDescription').split('\n')[0]}</strong><br />
              {t('sections.autoCreate.enabledDescription').split('\n')[1]}
            </div>

            <div className="section-description-error">
              <strong>{t('sections.autoCreate.disabledDescription').split('\n')[0]}</strong><br />
              {t('sections.autoCreate.disabledDescription').split('\n')[1]}
            </div>

            {saving && (
              <div className="admin-saving-msg">
                <div className="admin-spinner-inline"></div>
                <span>{t('messages.saving')}</span>
              </div>
            )}

            {saveSuccess && !saving && (
              <div className="admin-success-msg">
                ✓ {t('messages.saved')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Types d'équipes disponibles */}
      <div className="setup-section">
        <h2>{t('sections.privacyTypes.title')}</h2>
        <p className="help-text">
          {t('sections.privacyTypes.description')}
        </p>

        <div className="status-card">
          <div className="status-content">
            {allPrivacyTypes.length > 0 ? (
              <div className="admin-privacy-container">
                {allPrivacyTypes.map((type) => {
                  const isEnabled = enabledPrivacyTypes.includes(type.id);
                  const isLastEnabled = enabledPrivacyTypes.length === 1 && isEnabled;

                  return (
                    <div
                      key={type.id}
                      className={`admin-privacy-item ${isEnabled ? 'enabled' : 'disabled'}${saving ? ' saving' : ''}`}
                    >
                      <input
                        type="checkbox"
                        id={`privacy-type-${type.id}`}
                        checked={isEnabled}
                        onChange={() => handleTogglePrivacyType(type.id)}
                        disabled={saving || isLastEnabled}
                        className="admin-checkbox"
                        title={isLastEnabled ? t('messages.cannotDisableLastPrivacyTypeTooltip') : ""}
                      />
                      <label
                        htmlFor={`privacy-type-${type.id}`}
                        className={`admin-privacy-label-container${(saving || isLastEnabled) ? ' disabled' : ''}`}
                      >
                        <div className="admin-privacy-icon-row">
                          <span className="admin-privacy-icon">{type.icon}</span>
                          <span className="admin-privacy-name">{type.label}</span>
                        </div>
                        <div className="admin-privacy-desc">
                          {type.description}
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="errors-section">
                <p>⚠️ {t('sections.privacyTypes.loadingTypes')}</p>
              </div>
            )}

            {saveSuccess && (
              <div className="admin-success-msg">
                ✓ {t('sections.privacyTypes.successMessage')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gestion des administrateurs */}
      <div className="setup-section">
        <h2>{t('sections.administrators.title')}</h2>
        <p className="help-text">
          {t('sections.administrators.description')}
        </p>

        <div className="status-card">
          <div className="status-content">
            <div className="admin-status-header">
              <h3 className="admin-section-title">
                {t('sections.administrators.count', { count: administrators.length })}
              </h3>
              <button
                onClick={() => setShowAdminPeoplePicker(true)}
                disabled={saving}
                className="admin-add-btn"
              >
                {t('sections.administrators.add')}
              </button>
            </div>

            {administrators.length > 0 ? (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('sections.administrators.tableHeaders.name')}</th>
                      <th>{t('sections.administrators.tableHeaders.email')}</th>
                      <th>{t('sections.administrators.tableHeaders.addedDate')}</th>
                      <th>{t('sections.administrators.tableHeaders.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {administrators.map((admin) => (
                      <tr key={admin.id}>
                        <td>
                          <div className="table-name">{admin.displayName}</div>
                        </td>
                        <td>
                          <div className="table-label">{admin.email}</div>
                        </td>
                        <td>
                          <div className="table-label">
                            {new Date(admin.addedDate).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const isLastAdmin = administrators.length === 1;
                            const isCurrentUser = currentUserId && admin.id === currentUserId;
                            const isDisabled = saving || isLastAdmin || isCurrentUser;

                            return (
                              <button
                                onClick={() => handleRemoveAdministrator(admin.id)}
                                disabled={!!isDisabled}
                                className="admin-remove-btn"
                                title={
                                  isLastAdmin
                                    ? t('sections.administrators.removeTooltip.lastAdmin')
                                    : isCurrentUser
                                    ? t('sections.administrators.removeTooltip.currentUser')
                                    : t('sections.administrators.removeTooltip.default')
                                }
                              >
                                {t('sections.administrators.remove')}
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="errors-section">
                <p>⚠️ {t('sections.administrators.noAdmins')}</p>
                <p className="admin-mt-8 admin-text-small">
                  {t('sections.administrators.noAdminsHint')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Gestion des approbateurs */}
      <div className="setup-section">
        <h2>{t('sections.approvers.title')}</h2>
        <p className="help-text">
          {t('sections.approvers.description')}
        </p>
        <div className="warning-card admin-mb-16">
          <div className="admin-info-note-text">
            {t('sections.approvers.note')}
          </div>
        </div>

        {/* Paramètre de mode d'approbation */}
        <div className="status-card admin-mb-16">
          <div className="status-content">
            <label className="admin-approval-mode-label">
              {t('sections.approvalMode.title')}
            </label>

            <div className="admin-approval-warning-box">
              <div className="admin-approval-warning-text">
                {t('sections.approvalMode.warning')}
              </div>
            </div>

            <div className={`admin-approval-options-container${saving ? ' saving' : ''}`}>
              {/* Option 1: Une seule approbation suffit */}
              <label className={`admin-radio-option${settings?.notificationSettings?.approvalMode !== "basicAwaitAll" ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name="approvalMode"
                  value="basic"
                  checked={settings?.notificationSettings?.approvalMode !== "basicAwaitAll"}
                  onChange={() => onChangeApprovalMode("basic")}
                  disabled={saving}
                  className="admin-radio-input"
                />
                <div className="admin-radio-content">
                  <div className="admin-radio-title">
                    {t('sections.approvalMode.basic.title')}
                  </div>
                  <div className="admin-radio-desc">
                    {t('sections.approvalMode.basic.description')}
                  </div>
                </div>
              </label>

              {/* Option 2: Tous les approbateurs doivent répondre */}
              <label className={`admin-radio-option${settings?.notificationSettings?.approvalMode === "basicAwaitAll" ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name="approvalMode"
                  value="basicAwaitAll"
                  checked={settings?.notificationSettings?.approvalMode === "basicAwaitAll"}
                  onChange={() => onChangeApprovalMode("basicAwaitAll")}
                  disabled={saving}
                  className="admin-radio-input"
                />
                <div className="admin-radio-content">
                  <div className="admin-radio-title">
                    {t('sections.approvalMode.basicAwaitAll.title')}
                  </div>
                  <div className="admin-radio-desc">
                    {t('sections.approvalMode.basicAwaitAll.description')}
                  </div>
                </div>
              </label>
            </div>

            {saving && (
              <div className="admin-saving-msg">
                <div className="admin-spinner-inline"></div>
                <span>{t('messages.saving')}</span>
              </div>
            )}
          </div>
        </div>

        <div className="status-card">
          <div className="status-content">
            <div className="admin-status-header">
              <h3 className="admin-section-title">
                {t('sections.approvers.count', { count: selectedApprovers.length })}
              </h3>
              <button
                onClick={() => setShowPeoplePicker(true)}
                disabled={saving}
                className="admin-add-btn"
              >
                {t('sections.approvers.add')}
              </button>
            </div>

            {selectedApprovers.length > 0 ? (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('sections.approvers.tableHeaders.name')}</th>
                      <th>{t('sections.approvers.tableHeaders.email')}</th>
                      <th>{t('sections.approvers.tableHeaders.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedApprovers.map((approver) => (
                      <tr key={approver.id}>
                        <td>
                          <div className="table-name">{approver.displayName}</div>
                        </td>
                        <td>
                          <div className="table-label">{approver.mail || approver.userPrincipalName}</div>
                        </td>
                        <td>
                          {(() => {
                            const isLastApprover = selectedApprovers.length <= 1;
                            const isDisabled = saving || isLastApprover;

                            return (
                              <button
                                onClick={() => handleRemoveApprover(approver.id)}
                                disabled={isDisabled}
                                title={isLastApprover ? t('sections.approvers.removeTooltip.lastApprover') : t('sections.approvers.removeTooltip.default')}
                                className="admin-remove-btn"
                              >
                                {t('sections.approvers.remove')}
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="errors-section">
                <p>⚠️ {t('sections.approvers.noApprovers')}</p>
                <p className="admin-mt-8 admin-text-small">
                  {t('sections.approvers.noApproversHint')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Informations importantes */}
      <div className="setup-section">
        <div className="modal-section-info admin-how-it-works">
          <h3>
            {t('sections.howItWorks.title')}
          </h3>
          <ul>
            <li>
              <strong>{t('sections.howItWorks.whoCanRequest').split('?')[0]}?</strong> {t('sections.howItWorks.whoCanRequest').split('?')[1]}
            </li>
            <li>
              <strong>{t('sections.howItWorks.whoApproves').split('?')[0]}?</strong> {t('sections.howItWorks.whoApproves').split('?')[1]}
            </li>
            <li>
              <strong>{t('sections.howItWorks.howToApprove').split('?')[0]}?</strong> {t('sections.howItWorks.howToApprove').split('?')[1]}
            </li>
            <li className="admin-how-it-works-last">
              <strong>{t('sections.howItWorks.notifications').split(':')[0]}:</strong> {t('sections.howItWorks.notifications').split(':')[1]}
            </li>
          </ul>
        </div>
      </div>

      {/* PeoplePicker pour ajouter des approbateurs */}
      {showPeoplePicker && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h3 className="modal-title">
                  {t('sections.modals.selectApprovers.title')}
                </h3>
                <p className="modal-subtitle">
                  {t('sections.modals.selectApprovers.subtitle')}
                </p>
              </div>
              <button
                onClick={() => setShowPeoplePicker(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-body-paragraph">
                {t('sections.modals.selectApprovers.description')}
              </p>
              <PeoplePicker
                selectedUser=""
                onUserSelect={(user) => {
                  console.log("PeoplePicker user selected:", user);
                  handleAddApprover({
                    id: user.id,
                    displayName: user.displayName,
                    mail: user.mail || user.email || "",
                    userPrincipalName: user.userPrincipalName || ""
                  });
                  setShowPeoplePicker(false);
                }}
                placeholder={t('sections.modals.selectApprovers.placeholder')}
              />
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowPeoplePicker(false)}
                className="btn-tertiary"
              >
                {t('sections.modals.selectApprovers.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PeoplePicker pour ajouter des administrateurs */}
      {showAdminPeoplePicker && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-box">
            <div className="admin-modal-header">
              <div>
                <h3 className="admin-modal-title">
                  {t('sections.modals.selectAdministrators.title')}
                </h3>
                <p className="admin-modal-subtitle">
                  {t('sections.modals.selectAdministrators.subtitle')}
                </p>
              </div>
              <button
                onClick={() => setShowAdminPeoplePicker(false)}
                className="admin-modal-close-btn"
              >
                ×
              </button>
            </div>
            <div className="admin-modal-body">
              <p className="admin-modal-text">
                {t('sections.modals.selectAdministrators.description')}
              </p>
              <PeoplePicker
                selectedUser=""
                onUserSelect={(user) => {
                  console.log("Admin PeoplePicker user selected:", user);
                  handleAddAdministrator({
                    id: user.id,
                    displayName: user.displayName,
                    mail: user.mail || user.email || "",
                    userPrincipalName: user.userPrincipalName || ""
                  });
                }}
                placeholder={t('sections.modals.selectAdministrators.placeholder')}
              />
            </div>
            <div className="admin-modal-footer">
              <button
                onClick={() => setShowAdminPeoplePicker(false)}
                className="admin-modal-btn-cancel"
              >
                {t('sections.modals.selectAdministrators.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'avertissement */}
      {warningModal.isOpen && (
        <div className="admin-modal-overlay high-z">
          <div className="admin-modal-box small">
            <div className="admin-warning-content">
              <div className="admin-warning-icon">
                {warningModal.type === "warning" ? "⚠️" : "❌"}
              </div>
              <div className="admin-warning-body">
                <h3 className="admin-warning-title">
                  {warningModal.title}
                </h3>
                <p className="admin-warning-message">
                  {warningModal.message}
                </p>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                onClick={closeWarningModal}
                className="admin-modal-btn-confirm"
              >
                {t('sections.modals.warning.understood')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de changement de mode d'approbation */}
      {approvalModeConfirmModal.isOpen && (
        <div className="admin-modal-overlay confirmation-z">
          <div className="admin-modal-box confirmation">
            <h2 className="admin-modal-title large">
              {t('sections.modals.confirmApprovalMode.title')}
            </h2>

            <div className="admin-confirm-warning-box">
              <p className="admin-confirm-text">
                <strong>{t('sections.modals.confirmApprovalMode.warning').split(':')[0]}:</strong> {t('sections.modals.confirmApprovalMode.warning').split(':')[1]}
              </p>
            </div>

            {approvalModeConfirmModal.impactedRequests.length > 0 ? (
              <>
                <h3 className="admin-confirm-section-title">
                  {t('sections.modals.confirmApprovalMode.impactedRequests', { count: approvalModeConfirmModal.impactedRequests.length })}
                </h3>

                <div className="admin-confirm-list">
                  {approvalModeConfirmModal.impactedRequests.map((request, index) => (
                    <div
                      key={request.id}
                      className={`admin-confirm-item ${index % 2 === 0 ? 'even' : 'odd'}`}
                    >
                      <div className="admin-confirm-item-title">
                        {request.teamName}
                      </div>
                      <div className="admin-confirm-item-detail">
                        {t('sections.modals.confirmApprovalMode.requesterLabel')} : {request.requesterEmail}
                      </div>
                      <div className="admin-confirm-item-detail">
                        {getPrivacyLabel(request.privacy)}
                      </div>
                      <div className="admin-confirm-item-detail">
                        {t('sections.modals.confirmApprovalMode.approvalsLabel', { approved: request.approvedCount, total: request.totalApprovers })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="admin-confirm-empty">
                <p className="admin-confirm-empty-text">
                  {t('sections.modals.confirmApprovalMode.noImpactedRequests')}
                </p>
              </div>
            )}

            <div className="admin-modal-footer">
              <button
                onClick={cancelApprovalModeChange}
                className="admin-modal-btn-secondary"
              >
                {t('sections.modals.confirmApprovalMode.cancel')}
              </button>
              <button
                onClick={() => confirmApprovalModeChange(approvalModeConfirmModal.newMode!)}
                className="admin-modal-btn-confirm"
              >
                {t('sections.modals.confirmApprovalMode.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
